import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API } from "@/App";
import { cn } from "@/lib/utils";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  verticalListSortingStrategy, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  GripVertical, Eye, EyeOff, Copy, Trash2, Plus, Save, Send,
  Settings, X, Monitor, Tablet, Smartphone, Globe, RefreshCw,
  Maximize2, Minimize2, AlertTriangle, Loader2, Filter, Lock,
} from "lucide-react";
import { PreviewContext, HomepageSections } from "@/components/HomepageRenderer";

const SECTION_TYPES = [
  { value: "hero_slider", label: "Hero Slider" },
  { value: "category_pills", label: "Category Pills" },
  { value: "featured_channels", label: "Featured Channels" },
  { value: "trending_channels", label: "Trending Now" },
  { value: "recently_added", label: "Recently Added" },
  { value: "most_watched", label: "Most Watched" },
  { value: "news_channels", label: "News Channels" },
  { value: "entertainment_channels", label: "Entertainment" },
  { value: "religious_channels", label: "Religious" },
  { value: "sports_channels", label: "Sports" },
  { value: "music_channels", label: "Music" },
  { value: "kids_channels", label: "Kids" },
  { value: "recommended_channels", label: "Recommended" },
  { value: "premium_banner", label: "Premium Banner" },
  { value: "promo_banner", label: "Promotional Banner" },
  { value: "announcement_bar", label: "Announcement Bar" },
  { value: "custom_text", label: "Custom Text" },
  { value: "custom_image", label: "Custom Image" },
  { value: "app_download", label: "App Download" },
  { value: "newsletter", label: "Newsletter" },
];

const STATUS_COLORS = {
  published: "bg-green-500/10 text-green-400 border-green-500/30",
  draft: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  scheduled: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  archived: "bg-slate-500/10 text-slate-400 border-slate-500/30",
};

const DEVICE_WIDTHS = { desktop: 1440, tablet: 768, mobile: 390 };

// ============ SORTABLE SECTION ITEM ============

function SortableItem({ section, onToggle, onDuplicate, onDelete, onEdit }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : undefined };

  return (
    <div ref={setNodeRef} style={style} className={cn(
      "bg-slate-800/50 border rounded-xl p-4 flex items-center gap-4 transition-all",
      isDragging ? "border-blue-500 shadow-xl shadow-blue-500/10 opacity-80" : "border-slate-700/50",
      !section.is_visible && "opacity-50"
    )} data-testid={`section-${section.id}`}>
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-slate-600 hover:text-slate-400 touch-none">
        <GripVertical size={18} />
      </button>
      <div className="flex-grow min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <h4 className="text-sm font-semibold text-white truncate">{section.title_en || section.type}</h4>
          <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-semibold border", STATUS_COLORS[section.status] || STATUS_COLORS.draft)}>
            {section.status}
          </span>
        </div>
        <p className="text-[11px] text-slate-500">{section.type.replace(/_/g, " ")} · Order: {section.display_order}</p>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <button onClick={() => onToggle(section)} title={section.is_visible ? "Hide" : "Show"}
          className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white" data-testid={`toggle-${section.id}`}>
          {section.is_visible ? <Eye size={15} /> : <EyeOff size={15} />}
        </button>
        <button onClick={() => onEdit(section)} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white">
          <Settings size={15} />
        </button>
        <button onClick={() => onDuplicate(section)} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white">
          <Copy size={15} />
        </button>
        <button onClick={() => onDelete(section)} className="p-1.5 rounded-lg hover:bg-red-900/30 text-slate-400 hover:text-red-400">
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
}

// ============ MODAL ============

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <h3 className="text-base font-semibold text-white">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400"><X size={18} /></button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

// ============ PREVIEW PANEL ============

function PreviewPanel({ sections, previewData, previewLoading, previewError, onRefresh, onClose }) {
  const { i18n } = useTranslation();
  const [device, setDevice] = useState("desktop");
  const [lang, setLang] = useState("en");
  const [filterPublishable, setFilterPublishable] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);

  const deviceWidth = DEVICE_WIDTHS[device];

  useEffect(() => {
    if (!containerRef.current) return;
    const updateScale = () => {
      if (!containerRef.current) return;
      const avail = containerRef.current.getBoundingClientRect().width;
      setScale(Math.min(1, avail / deviceWidth));
    };
    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [deviceWidth]);

  // Use the real i18n translation function bound to the selected preview language.
  // getFixedT returns a t() for a specific locale without changing the global language.
  const previewT = useMemo(() => i18n.getFixedT(lang), [i18n, lang]);

  // Merge local section ordering with API preview data
  const mergedSections = (() => {
    if (!previewData) return [];
    // Use local sections for ordering and visibility, but enrich with API _preview_status
    const apiSectionMap = {};
    (previewData.sections || []).forEach(s => { apiSectionMap[s.id] = s; });
    return sections.map((localS, i) => {
      const apiS = apiSectionMap[localS.id] || {};
      return { ...apiS, ...localS, display_order: i + 1, _preview_status: apiS._preview_status || localS.status || "draft" };
    });
  })();

  const sectionData = previewData ? {
    sliders: previewData.sliders || [],
    banners: previewData.banners || [],
    featured_channels: previewData.featured_channels || [],
    trending_channels: previewData.trending_channels || [],
    recently_added: previewData.recently_added || [],
    categories: previewData.categories || [],
    countries: previewData.countries || [],
    allChannels: previewData.featured_channels || [],
    settings: {},
    isPremium: false,
    onSelectCategory: () => {},
    selectedCategory: null,
  } : null;

  const panelClasses = fullscreen
    ? "fixed inset-0 z-[60] bg-[#050814] flex flex-col"
    : "mt-6 border border-slate-700/50 rounded-2xl bg-slate-900/50 overflow-hidden";

  return (
    <div className={panelClasses} data-testid="preview-panel">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 bg-slate-900 border-b border-slate-800" data-testid="preview-toolbar">
        {/* Device switcher */}
        <div className="flex items-center bg-slate-800 rounded-lg p-0.5">
          {[
            { key: "desktop", icon: Monitor, label: "Desktop" },
            { key: "tablet", icon: Tablet, label: "Tablet" },
            { key: "mobile", icon: Smartphone, label: "Mobile" },
          ].map(({ key, icon: Icon, label }) => (
            <button key={key} onClick={() => setDevice(key)}
              className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all",
                device === key ? "bg-blue-600 text-white shadow" : "text-slate-400 hover:text-white")}
              data-testid={`preview-device-${key}`}>
              <Icon size={14} /><span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>

        {/* Language switcher */}
        <div className="flex items-center bg-slate-800 rounded-lg p-0.5">
          {["en", "fr"].map(l => (
            <button key={l} onClick={() => setLang(l)}
              className={cn("px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5",
                lang === l ? "bg-blue-600 text-white shadow" : "text-slate-400 hover:text-white")}
              data-testid={`preview-lang-${l}`}>
              <Globe size={12} />{l.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Filter toggle */}
        <button onClick={() => setFilterPublishable(!filterPublishable)}
          className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border",
            filterPublishable ? "bg-green-600/20 text-green-400 border-green-500/30" : "bg-slate-800 text-slate-400 border-slate-700 hover:text-white")}
          data-testid="preview-filter-toggle">
          <Filter size={12} />{filterPublishable ? "Publishable Only" : "All Content"}
        </button>

        <div className="flex-grow" />

        {/* Actions */}
        <button onClick={onRefresh} className="p-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-400 hover:text-white transition-colors" title="Refresh Preview" data-testid="preview-refresh-btn">
          <RefreshCw size={14} />
        </button>
        <button onClick={() => setFullscreen(!fullscreen)} className="p-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-400 hover:text-white transition-colors" title={fullscreen ? "Exit Fullscreen" : "Fullscreen"} data-testid="preview-fullscreen-btn">
          {fullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
        </button>
        <button onClick={() => { setFullscreen(false); onClose(); }} className="p-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-400 hover:text-red-400 transition-colors" title="Close Preview" data-testid="preview-close-btn">
          <X size={14} />
        </button>
      </div>

      {/* Draft warning banner */}
      <div className="px-4 py-2 bg-amber-500/10 border-b border-amber-500/20 flex items-center justify-center gap-2" data-testid="preview-draft-warning">
        <AlertTriangle size={14} className="text-amber-400" />
        <span className="text-xs font-bold text-amber-400 tracking-wider">
          {lang === "fr" ? "APERÇU BROUILLON — NON PUBLIC" : "DRAFT PREVIEW — NOT PUBLIC"}
        </span>
        <AlertTriangle size={14} className="text-amber-400" />
      </div>

      {/* Preview content area */}
      <div className={cn("flex-1 overflow-auto", fullscreen ? "p-4" : "p-4")} ref={containerRef}>
        {/* Loading state */}
        {previewLoading && (
          <div className="flex flex-col items-center justify-center py-20 gap-4" data-testid="preview-loading">
            <Loader2 size={32} className="text-blue-400 animate-spin" />
            <p className="text-sm text-slate-400">Loading preview...</p>
            <div className="w-full max-w-lg space-y-3">
              <div className="h-48 rounded-xl bg-slate-800/50 animate-pulse" />
              <div className="flex gap-2">{[1,2,3].map(i => <div key={i} className="h-8 w-20 rounded-lg bg-slate-800/50 animate-pulse" />)}</div>
              <div className="h-32 rounded-xl bg-slate-800/50 animate-pulse" />
            </div>
          </div>
        )}

        {/* Error state */}
        {!previewLoading && previewError === "auth_expired" && (
          <div className="flex flex-col items-center justify-center py-20 gap-4" data-testid="preview-auth-error">
            <div className="w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
              <Lock size={24} className="text-amber-400" />
            </div>
            <h3 className="text-lg font-semibold text-white">Session Expired</h3>
            <p className="text-sm text-slate-400">Your authentication has expired. Please log in again.</p>
            <button onClick={() => window.location.href = "/admin/login"} className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-500" data-testid="preview-relogin-btn">
              Log In Again
            </button>
          </div>
        )}

        {!previewLoading && previewError === "access_denied" && (
          <div className="flex flex-col items-center justify-center py-20 gap-4" data-testid="preview-access-denied">
            <div className="w-14 h-14 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
              <Lock size={24} className="text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-white">Access Denied</h3>
            <p className="text-sm text-slate-400">You do not have permission to preview the homepage.</p>
          </div>
        )}

        {!previewLoading && previewError && previewError !== "auth_expired" && previewError !== "access_denied" && (
          <div className="flex flex-col items-center justify-center py-20 gap-4" data-testid="preview-error">
            <div className="w-14 h-14 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
              <AlertTriangle size={24} className="text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-white">The preview could not be loaded.</h3>
            <p className="text-sm text-slate-400">An error occurred while fetching preview data.</p>
            <div className="flex gap-3">
              <button onClick={onRefresh} className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-500" data-testid="preview-retry-btn">
                Retry
              </button>
              <button onClick={onClose} className="px-4 py-2 rounded-xl bg-slate-800 border border-slate-700 text-slate-300 text-sm font-medium hover:bg-slate-700">
                Return to Homepage Builder
              </button>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!previewLoading && !previewError && previewData && mergedSections.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 gap-4" data-testid="preview-empty">
            <Settings size={32} className="text-slate-500" />
            <h3 className="text-lg font-semibold text-white">No homepage content is available for preview.</h3>
            <p className="text-sm text-slate-400">Add some sections to the Homepage Builder to see a preview.</p>
          </div>
        )}

        {/* Actual preview */}
        {!previewLoading && !previewError && previewData && sectionData && (
          <div className="flex justify-center">
            <div className="w-full" style={{ maxWidth: deviceWidth + "px" }}>
              {/* Device frame indicator */}
              <div className="text-center mb-2">
                <span className="text-[10px] text-slate-600 font-mono">{deviceWidth}px — {device.charAt(0).toUpperCase() + device.slice(1)}</span>
              </div>
              <div className="overflow-hidden rounded-xl border border-slate-700/30 bg-[#050814]"
                style={{ height: `auto` }}>
                <div style={{
                  width: deviceWidth + "px",
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                }}>
                  <PreviewContext.Provider value={{ previewMode: true, filterPublishable }}>
                    <div className="px-4 sm:px-6 lg:px-8 py-6">
                      <HomepageSections
                        sections={mergedSections}
                        data={sectionData}
                        locale={lang}
                        t={previewT}
                        filterPublishable={filterPublishable}
                      />
                    </div>
                  </PreviewContext.Provider>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Note about unsaved changes */}
      <div className="px-4 py-2 bg-slate-900 border-t border-slate-800 text-center">
        <p className="text-[10px] text-slate-600">
          Preview reflects section order and visibility from local state. Sliders and banners load from the latest saved draft.
        </p>
      </div>
    </div>
  );
}

// ============ MAIN COMPONENT ============

export default function AdminHomepage() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [newType, setNewType] = useState("custom_text");
  const [newTitleEn, setNewTitleEn] = useState("");
  const [newTitleFr, setNewTitleFr] = useState("");

  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const fetchSections = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/homepage/sections`, { withCredentials: true });
      setSections(res.data ?? []);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load homepage sections");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchSections(); }, [fetchSections]);

  const fetchPreview = useCallback(async () => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const res = await axios.get(`${API}/admin/homepage/preview`, { withCredentials: true });
      if (res.data?.success) {
        setPreviewData(res.data.data);
      } else {
        setPreviewError("error");
      }
    } catch (e) {
      if (e.response?.status === 401) setPreviewError("auth_expired");
      else if (e.response?.status === 403) setPreviewError("access_denied");
      else setPreviewError("error");
    }
    setPreviewLoading(false);
  }, []);

  const handleOpenPreview = () => {
    setPreviewOpen(true);
    fetchPreview();
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = sections.findIndex(s => s.id === active.id);
      const newIndex = sections.findIndex(s => s.id === over.id);
      setSections(arrayMove(sections, oldIndex, newIndex));
      setHasChanges(true);
    }
  };

  const saveOrder = async () => {
    try {
      await axios.put(`${API}/admin/homepage/sections/reorder`, {
        order: sections.map(s => s.id),
      }, { withCredentials: true });
      setHasChanges(false);
      fetchSections();
    } catch { /* save failed silently */ }
  };

  const toggleVisibility = async (section) => {
    // Update local state immediately for preview responsiveness
    setSections(prev => prev.map(s => s.id === section.id ? { ...s, is_visible: !s.is_visible } : s));
    await axios.put(`${API}/admin/homepage/sections/${section.id}`, {
      is_visible: !section.is_visible,
    }, { withCredentials: true });
    fetchSections();
  };

  const duplicateSection = async (section) => {
    await axios.post(`${API}/admin/homepage/sections/${section.id}/duplicate`, {}, { withCredentials: true });
    fetchSections();
  };

  const deleteSection = async (section) => {
    if (!confirm(`Delete "${section.title_en}"?`)) return;
    await axios.delete(`${API}/admin/homepage/sections/${section.id}`, { withCredentials: true });
    fetchSections();
  };

  const addSection = async () => {
    await axios.post(`${API}/admin/homepage/sections`, {
      type: newType, title_en: newTitleEn || SECTION_TYPES.find(t => t.value === newType)?.label,
      title_fr: newTitleFr || "", is_visible: false,
    }, { withCredentials: true });
    setAddModal(false); setNewTitleEn(""); setNewTitleFr(""); fetchSections();
  };

  const publishAll = async () => {
    await axios.post(`${API}/admin/homepage/publish`, {
      action: "publish", section_ids: sections.map(s => s.id),
    }, { withCredentials: true });
    fetchSections();
  };

  const updateSection = async () => {
    if (!editModal) return;
    await axios.put(`${API}/admin/homepage/sections/${editModal.id}`, editModal, { withCredentials: true });
    setEditModal(null); fetchSections();
  };

  const draftCount = sections.filter(s => s.status === "draft").length;
  const publishedCount = sections.filter(s => s.status === "published").length;
  const hiddenCount = sections.filter(s => !s.is_visible).length;

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse" data-testid="homepage-loading">
        <div className="flex items-center justify-between"><div className="h-5 w-40 bg-slate-800 rounded" /><div className="h-9 w-32 bg-slate-800 rounded-lg" /></div>
        {[1, 2, 3, 4].map(i => <div key={i} className="h-16 bg-slate-800/50 rounded-xl border border-slate-700/30" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4" data-testid="homepage-error">
        <div className="w-14 h-14 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
          <Settings size={24} className="text-red-400" />
        </div>
        <h3 className="text-lg font-semibold text-white">Failed to load homepage sections</h3>
        <p className="text-sm text-slate-400">{error}</p>
        <button onClick={fetchSections} className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-500" data-testid="homepage-retry-btn">
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Stats + Actions Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500">{sections.length} sections</span>
          <span className="text-xs text-green-400">{publishedCount} published</span>
          <span className="text-xs text-amber-400">{draftCount} drafts</span>
          <span className="text-xs text-slate-500">{hiddenCount} hidden</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {hasChanges && (
            <button onClick={saveOrder} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-500 flex items-center gap-2" data-testid="save-order-btn">
              <Save size={14} /> Save Order
            </button>
          )}
          <button onClick={() => setAddModal(true)} className="px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm font-medium hover:bg-slate-700 flex items-center gap-2" data-testid="add-section-btn">
            <Plus size={14} /> Add Section
          </button>
          <button onClick={publishAll} className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-500 flex items-center gap-2" data-testid="publish-all-btn">
            <Send size={14} /> Publish All
          </button>
          <button onClick={previewOpen ? () => setPreviewOpen(false) : handleOpenPreview}
            className={cn("px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all",
              previewOpen
                ? "bg-amber-600 text-white hover:bg-amber-500"
                : "bg-purple-600 text-white hover:bg-purple-500")}
            data-testid="preview-toggle-btn">
            <Eye size={14} /> {previewOpen ? "Close Preview" : "Preview"}
          </button>
        </div>
      </div>

      {/* Section List with Drag & Drop */}
      <div className="space-y-2" data-testid="sections-list">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
            {sections.map(section => (
              <SortableItem key={section.id} section={section}
                onToggle={toggleVisibility} onDuplicate={duplicateSection}
                onDelete={deleteSection} onEdit={() => setEditModal({ ...section })} />
            ))}
          </SortableContext>
        </DndContext>
        {sections.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-4" data-testid="homepage-empty">
            <div className="w-16 h-16 rounded-2xl bg-slate-800 border border-slate-700/50 flex items-center justify-center">
              <Settings size={28} className="text-slate-500" />
            </div>
            <div className="text-center">
              <h3 className="text-base font-semibold text-white mb-1">No homepage sections yet</h3>
              <p className="text-sm text-slate-500 max-w-sm">Create your first homepage section to start building the page.</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setAddModal(true)} className="px-5 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-500 flex items-center gap-2" data-testid="create-first-section-btn">
                <Plus size={14} /> Create Section
              </button>
            </div>
          </div>
        )}
      </div>

      {/* PREVIEW PANEL */}
      {previewOpen && (
        <PreviewPanel
          sections={sections}
          previewData={previewData}
          previewLoading={previewLoading}
          previewError={previewError}
          onRefresh={fetchPreview}
          onClose={() => setPreviewOpen(false)}
        />
      )}

      {/* Add Section Modal */}
      {addModal && (
        <Modal title="Add Section" onClose={() => setAddModal(false)}>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">Section Type</label>
              <select value={newType} onChange={e => setNewType(e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" data-testid="section-type-select">
                {SECTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1.5 block">Title (EN)</label>
                <input type="text" value={newTitleEn} onChange={e => setNewTitleEn(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" placeholder="Section title..." />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1.5 block">Titre (FR)</label>
                <input type="text" value={newTitleFr} onChange={e => setNewTitleFr(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" placeholder="Titre de section..." />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setAddModal(false)} className="px-4 py-2 rounded-lg bg-slate-800 text-slate-400 text-sm">Cancel</button>
              <button onClick={addSection} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium" data-testid="confirm-add-section">Add Section</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Section Modal */}
      {editModal && (
        <Modal title="Edit Section" onClose={() => setEditModal(null)}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1.5 block">Title (EN)</label>
                <input type="text" value={editModal.title_en || ""} onChange={e => setEditModal({ ...editModal, title_en: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1.5 block">Titre (FR)</label>
                <input type="text" value={editModal.title_fr || ""} onChange={e => setEditModal({ ...editModal, title_fr: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">Status</label>
              <select value={editModal.status || "draft"} onChange={e => setEditModal({ ...editModal, status: e.target.value })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white">
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="scheduled">Scheduled</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            {editModal.status === "scheduled" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block">Publish At</label>
                  <input type="datetime-local" value={editModal.publish_at?.slice(0, 16) || ""} onChange={e => setEditModal({ ...editModal, publish_at: e.target.value ? new Date(e.target.value).toISOString() : null })}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block">Unpublish At</label>
                  <input type="datetime-local" value={editModal.unpublish_at?.slice(0, 16) || ""} onChange={e => setEditModal({ ...editModal, unpublish_at: e.target.value ? new Date(e.target.value).toISOString() : null })}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" />
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setEditModal(null)} className="px-4 py-2 rounded-lg bg-slate-800 text-slate-400 text-sm">Cancel</button>
              <button onClick={updateSection} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium" data-testid="save-section-btn">Save</button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
